import { useNavigate, useSearchParams } from 'react-router-dom'
import { NewListWizard, WIZARD_TITLE } from 'app/components/lists/new/NewListWizard'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { List } from 'app/generated/graphql'
import type { ListVisibility } from 'app/types/list-visibility'
import { useListStore } from 'app/stores/StoreContext'
import { manualListPath } from 'lib/lists'
import { logError } from 'lib/logging'

/** The one door to a new list: the sidebar's plus button and Manage lists both open it. */
export function useNewListWizard({
  recordTypes,
  canCreateManualList,
  onListsChanged,
}: {
  recordTypes: string[]
  canCreateManualList: boolean
  onListsChanged: () => void
}) {
  const listStore = useListStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const workspaceQuery = searchParams.has(`wId`) ? `?wId=${searchParams.get(`wId`)}` : ``

  const createManualList = async (data: { [k: string]: FormDataEntryValue }, visibility: ListVisibility) => {
    const firstStage =
      data.stage && data.color ? { name: data.stage as string, color: data.color as string } : undefined
    const result = await listStore.create({
      name: data.name as string,
      entityType: `Contact`,
      stages: firstStage ? [firstStage] : undefined,
      visibility,
    })
    if (result.error) {
      logError(`Error creating a manual list`, result.error.message)
      notifyError(`That list could not be created. ${result.error.message}`)
      return
    }
    // Read straight off this mutation's own response rather than `listStore.current`:
    // `current` is shared store state, so a `fetchOne` for some other list landing in the
    // gap between this await and the read below could send the user to the wrong list.
    const created = (result.data as { createList?: List } | undefined)?.createList
    // The API assigns a stage its id, so the first stage can only be made the default once it exists.
    const firstStageId = firstStage ? created?.stages?.[0]?.id : undefined
    if (created?.id && firstStageId) {
      const defaulted = await listStore.update(created.id, { defaultStageId: firstStageId })
      if (defaulted.error) logError(`Error making a new list's first stage its default`, defaulted.error.message)
    }
    notifySuccess(`List created`)
    closeModal()
    // The list page resolves ids from the List store's own read, which is cache-first.
    await listStore.fetchList(undefined, { requestPolicy: `network-only` })
    if (created?.id) navigate(`${manualListPath(created.id)}${workspaceQuery}`)
    onListsChanged()
  }

  const open = () => {
    showModal(
      // No padding here: each step pads itself (see `NewListWizard`), because the filter step's
      // form runs its footer edge to edge.
      <NewListWizard
        recordTypes={recordTypes}
        canCreateManualList={canCreateManualList}
        createManualList={createManualList}
        onListsChanged={onListsChanged}
      />,
      { title: WIZARD_TITLE, isFullScreen: false, overflowHidden: false, useRadix: true, removePadding: true },
    )
  }

  return { open }
}
